import styled from '@emotion/styled';
import { borderRadius, color } from '@shared/constants/Styles';
import { forwardRef } from 'react';
import { IconName } from '../images/IconMap';
import Icons from '../images/Icons';
import { MixinFocusVisible } from '../structure/DefaultStyles';
import { TextSize80 } from '../text/Typography';

interface ITabButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isActive: boolean;
    statusAlert?: 'warning' | 'error' | 'success';
    iconName?: IconName;
    iconColor?: string;
}

const TabButton = forwardRef<HTMLButtonElement, ITabButtonProps>(
    ({ isActive, statusAlert, children, iconName, iconColor, disabled, ...restProps }, ref) => {
        return (
            <TabButtonStyle statusAlert={statusAlert} isActive={isActive} disabled={disabled} {...restProps} ref={ref}>
                {(isActive || iconName) && <Icons name={iconName || 'check'} size={16} color={iconColor} />}
                {children}
            </TabButtonStyle>
        );
    }
);

TabButton.displayName = 'TabButton';

export default TabButton;

const TabButtonStyle = styled.button<{ isActive: boolean; statusAlert?: 'warning' | 'error' | 'success' }>`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0.5rem;
    ${TextSize80};
    border: ${(props) => (props.isActive ? `1px solid ${props.theme.color.text}` : '1px solid transparent')};
    background: ${(props) => (props.isActive ? props.theme.color.accent : props.theme.color.cardHover)};
    color: ${(props) => (props.isActive ? props.theme.color.background : props.theme.button.secondary.text)};
    ${(props) => props.isActive && `text-decoration: underline;`};
    cursor: pointer;
    border-radius: ${borderRadius.md};
    transition: all 0.2s ease;
    ${(props) =>
        props.statusAlert === 'warning' && ` background: ${color.accent_2_light}; color: ${color.primary_dark};`}
    ${(props) => props.statusAlert === 'error' && ` color: ${color.bg_light}; background: ${color.error};`}
    ${(props) => props.statusAlert === 'success' && ` background: ${color.success}; color: ${color.bg_light};`}

    &:hover {
        background: ${(props) => props.theme.button.secondary.text};
        color: ${(props) => props.theme.color.background};
    }
    &:focus {
        outline: none !important;
        box-shadow: none !important;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    ${MixinFocusVisible}
`;
